import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraCompareColumns } from '@mono/ui-components/captains';
import { SpecraButton, SpecraHeading, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCheckboxField, SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraPersonOptionContent, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useRef, useState } from 'react';
import { useRegistryEntitySearch } from '../../../components/SharedRecord/useRegistryEntitySearch';
import { decideEmploymentCandidate } from './decide';
const T = Testids.registryEmployment;
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
const DUPLICATE_PREFIX = 'A person with this name';
/** "2024-03-15" → "Mar 2024"; anything unparseable is shown as written. */ export function monthYear(iso) {
    const m = /^(\d{4})-(\d{2})/.exec(iso);
    return m && MONTHS[Number(m[2]) - 1] ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : iso;
}
/** The day before an ISO date, in UTC so no zone can move it. */ export function dayBefore(iso) {
    const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return iso;
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
}
const isNameConflict = (e)=>{
    const err = ConnectError.from(e);
    return err.code === Code.FailedPrecondition && err.rawMessage.startsWith(DUPLICATE_PREFIX);
};
const join = (...parts)=>parts.filter(Boolean).join(', ');
export function EmploymentChangeItem({ item, onRecorded }) {
    const ec = item.employmentChange;
    const [endPrevious, setEndPrevious] = useState(!!ec?.endPreviousEmploymentId);
    const [conflict, setConflict] = useState();
    const [newPerson, setNewPerson] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [reason, setReason] = useState('');
    const [query, setQuery] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const pending = useRef(false);
    const search = useRegistryEntitySearch('CONTACT', query);
    const options = useMemo(()=>search.options.map((o)=>({
                value: o.registryId,
                label: o.label,
                textValue: o.label,
                content: /*#__PURE__*/ _jsx(SpecraPersonOptionContent, {
                    name: o.label,
                    description: o.sublabel
                })
            })), [
        search.options
    ]);
    if (!ec) return null;
    const person = ec.contactName || 'this person';
    const since = monthYear(ec.proposedStartDate);
    const newJob = join(ec.proposedTitle, ec.proposedOrgName);
    const hasPrevious = !!ec.endPreviousEmploymentId || !!ec.currentTitle || !!ec.currentEmployer;
    const send = async (args)=>{
        if (pending.current) return;
        pending.current = true;
        setBusy(true);
        setError('');
        try {
            await decideEmploymentCandidate(args);
            await onRecorded();
        } catch (e) {
            if (args.decision === 'ACCEPT' && isNameConflict(e)) setConflict(ConnectError.from(e).rawMessage);
            else setError(e instanceof Error ? e.message : 'Could not record that decision. Try again.');
        } finally{
            pending.current = false;
            setBusy(false);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4 p-4",
        "aria-busy": busy,
        children: [
            /*#__PURE__*/ _jsx(SpecraHeading, {
                level: 2,
                size: "ui",
                "data-testid": Testids.registryReview.question,
                children: `Add a new job to ${person}'s history: ${ec.proposedTitle}, ${ec.proposedOrgName}, from ${since}?`
            }),
            /*#__PURE__*/ _jsx(SpecraCompareColumns, {
                framed: "cards",
                sides: [
                    {
                        id: 'current',
                        eyebrow: 'Current',
                        rows: [
                            {
                                id: 'now',
                                label: 'Now',
                                value: join(ec.currentTitle, ec.currentEmployer) || '—'
                            },
                            {
                                id: 'since',
                                label: 'Since',
                                value: ec.currentStartDate ? monthYear(ec.currentStartDate) : '—'
                            }
                        ]
                    },
                    {
                        id: 'after',
                        eyebrow: 'After',
                        rows: [
                            {
                                id: 'new',
                                label: 'New job',
                                value: `${newJob} from ${since}`
                            },
                            ...hasPrevious ? [
                                {
                                    id: 'previous',
                                    label: 'Previous job',
                                    value: endPrevious && ec.endPreviousEmploymentId ? `ends ${dayBefore(ec.proposedStartDate)}` : 'kept'
                                }
                            ] : []
                        ]
                    }
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": T.evidence,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Evidence"
                    }),
                    /*#__PURE__*/ _jsxs("ul", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(SpecraText, {
                                    children: ec.sourceLabel
                                })
                            }),
                            ec.uploaderName && /*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(SpecraText, {
                                    tone: "muted",
                                    children: `uploaded by ${ec.uploaderName}`
                                })
                            })
                        ]
                    })
                ]
            }),
            conflict !== undefined && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-3",
                "data-testid": T.conflict,
                children: [
                    /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "warning",
                        children: conflict
                    }),
                    /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Choose the existing person",
                        placeholder: "Search people",
                        searchPlaceholder: "Search people…",
                        options: options,
                        shouldFilter: false,
                        loading: search.loading && query.trim().length >= 2,
                        searchValue: query,
                        onSearchValueChange: setQuery,
                        disabled: busy,
                        emptyMessage: query.trim().length < 2 ? 'Type at least 2 characters.' : 'No person matches.',
                        onOptionChange: (opt)=>opt && void send({
                                id: item.id,
                                decision: 'CHOOSE_EXISTING',
                                endPrevious,
                                bindContactId: opt.value
                            }),
                        triggerProps: {
                            'data-testid': T.choosePerson
                        }
                    }),
                    /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                        label: "This is a new person",
                        "data-testid": T.newPerson,
                        controlProps: {
                            checked: newPerson,
                            disabled: busy,
                            onCheckedChange: (c)=>setNewPerson(c === true)
                        }
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: error
            }),
            ec.endPreviousEmploymentId && /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                label: "End their previous job",
                "data-testid": T.endPrevious,
                controlProps: {
                    checked: endPrevious,
                    disabled: busy,
                    onCheckedChange: (c)=>setEndPrevious(c === true)
                }
            }),
            rejecting && /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Why isn't this right?",
                required: true,
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "data-testid": T.rejectReason,
                    value: reason,
                    disabled: busy,
                    onChange: (e)=>setReason(e.target.value),
                    maxLength: 500
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.add,
                        disabled: busy || rejecting,
                        onClick: ()=>void send({
                                id: item.id,
                                decision: 'ACCEPT',
                                endPrevious,
                                mintNewContact: !ec.contactId || newPerson,
                                forceCreate: newPerson
                            }),
                        children: "Add the job"
                    }),
                    rejecting ? /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.rejectConfirm,
                        variant: "destructive",
                        disabled: busy || !reason.trim(),
                        onClick: ()=>void send({
                                id: item.id,
                                decision: 'REJECT',
                                endPrevious: false,
                                reason: reason.trim()
                            }),
                        children: "Confirm: not this person"
                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.reject,
                        variant: "secondary",
                        disabled: busy,
                        onClick: ()=>setRejecting(true),
                        children: "Not this person"
                    })
                ]
            })
        ]
    });
}
